import * as Api from '@ViewModels';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { buildPagedSortQueryParams } from '../utils/pagedSortQueryParams';
import { IVolunteerAttendance, IVolunteerFilter, isVolunteerBoardDisabledError } from './models';

export const VOLUNTEER_ATTENDANCE_KEY = '/volunteering/attendance';

/**
 * Cross-activity attendance, newest date first. The sort is sent rather than left to the endpoint's
 * default, because callers order the next-shift lookup and the history table by it. The endpoint
 * rejects a filter without a top-level ContactId criterion with a 400, so it is only ever a scoped
 * history — never an account-wide list.
 */
export const useVolunteerAttendanceQuery = ({
	filter,
	pageSize = 50,
	enabled = true,
	impersonationContext,
}: {
	filter: IVolunteerFilter;
	pageSize?: number;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}) => {
	const userSession = useUserSession();
	return useInfiniteQuery({
		enabled,
		queryKey: [VOLUNTEER_ATTENDANCE_KEY, filter, pageSize, impersonationContext?.account?.id],
		queryFn: async ({ pageParam }) => {
			const value = await userSession.webServiceHelper.callAsync<Api.IPagedCollection<IVolunteerAttendance>>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: 'volunteering/attendance/filter',
					queryParams: buildPagedSortQueryParams({
						pageSize,
						pageToken: pageParam,
						sortBy: 'date',
						sort: 'desc',
						expand: 'activity',
					}),
				}),
				'POST',
				filter
			);
			return value ?? { values: [] as IVolunteerAttendance[] };
		},
		getNextPageParam: lastPage => lastPage.pageToken || undefined,
		initialPageParam: '',
		meta: {
			toastErrorMessage: (error: Error) =>
				isVolunteerBoardDisabledError(error) ? '' : 'Failed to load this contact’s volunteering history',
		},
	});
};
